import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Truck, Headset, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, buildWhatsAppUrl, generalWhatsAppMessage, normalizeTaxonomy } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import ProductCard from "@/components/ProductCard";
import useSEO from "@/hooks/useSEO";

const TRUST = [
  { icon: Wrench, title: "Mesin Berkualitas", desc: "Pilihan mesin dan peralatan industri untuk kebutuhan proyek dan workshop." },
  { icon: Headset, title: "Dukungan Teknis", desc: "Konsultasikan kebutuhan mesin Anda dengan tim Teknik Diesel Indonesia." },
  { icon: ShieldCheck, title: "Spare Parts & Service", desc: "Dukungan spare parts serta layanan perawatan dan perbaikan mesin." },
  { icon: Truck, title: "Pengiriman Indonesia", desc: "Melayani kebutuhan pelanggan dari berbagai wilayah di Indonesia." },
];

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 mb-2">
      <span className="w-8 h-0.5 bg-[#FF9418]" />
      <span className="font-mono-tech text-xs uppercase tracking-widest text-[#FF9418] font-semibold">{children}</span>
    </div>
  );
}

/* Compact, image-first homepage category card. Uses admin `image` when
   available and falls back to a subtle industrial pattern + gear icon. */
function CategoryCard({ cat }) {
  const testid = `category-card-${(cat.slug || cat.name || "").toLowerCase().replace(/\s+/g, "-")}`;
  return (
    <Link
      to={`/produk?category=${encodeURIComponent(cat.name)}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-[#E5E5E5] hover:border-[#FF9418] hover:shadow-md bg-white transition"
      data-testid={testid}
    >
      <div className="relative aspect-[4/3] bg-[#F8F8F8] overflow-hidden">
        {cat.image ? (
          <img src={cat.image} alt={cat.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 14px, rgba(255,148,24,0.06) 14px 28px)" }}>
            <div className="w-12 h-12 flex items-center justify-center bg-[#FFF4E8] border border-[#FFD3A2] rounded-lg">
              <Wrench size={22} className="text-[#FF9418]" />
            </div>
          </div>
        )}
      </div>
      <div className="p-3 sm:p-4">
        <div className="font-display font-bold text-[#171717] text-sm sm:text-base leading-tight">{cat.name}</div>
        {cat.count > 0 && (
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#737373] mt-1">
            {cat.count} produk
          </div>
        )}
        <div className="flex items-center gap-1 text-xs font-mono-tech uppercase tracking-widest text-[#737373] group-hover:text-[#FF9418] transition mt-2">
          Lihat Katalog <ArrowRight size={12} />
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [homepageCategories, setHomepageCategories] = useState([]);
  const { settings } = useSettings();
  const wa = settings?.whatsapp;

  useSEO({
    title: settings?.seo_default_title || "Teknik Diesel Indonesia — Mesin & Peralatan Industri",
    description: settings?.seo_default_description || "Penyedia kompresor Airman, bar bender, bar cutter, bar roller, spare parts, dan layanan servis mesin industri di Indonesia.",
    path: "/",
    image: settings?.seo_og_image || "https://teknikdiesel.com/logo.png",
  });

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 8 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/products", { params: { sort: "newest", limit: 4 } }).then((r) => setLatest(r.data)).catch(() => {});
    api.get("/categories", { params: { scope: "homepage" } })
      .then((r) => setHomepageCategories((r.data || []).map((c) => normalizeTaxonomy(c, "category"))))
      .catch(() => setHomepageCategories([]));
    // JSON-LD Organization
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "ld-org";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Teknik Diesel Indonesia",
      "alternateName": "Teknik Diesel Indonesia — General Technical Supply",
      "url": "https://teknikdiesel.com",
      "logo": "https://teknikdiesel.com/logo.png",
      "description": "Penyedia kompresor Airman, bar bender & cutter Takeda/Toyo, bar roller, spare parts, dan servis mesin industri di Indonesia.",
    });
    document.head.querySelector("#ld-org")?.remove();
    document.head.appendChild(script);
    return () => { document.head.querySelector("#ld-org")?.remove(); };
  }, []);

  return (
    <div className="bg-white">
      {/* HERO — typography-based, admin-editable optional workshop image */}
      <section className="relative overflow-hidden bg-white border-b border-[#F0F0F0]" data-testid="hero-section">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FFF4E8] rounded-full opacity-60 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center relative">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#FFF4E8] border border-[#FFD3A2] rounded-full mb-5">
              <span className="w-1.5 h-1.5 bg-[#FF9418] rounded-full" />
              <span className="font-mono-tech text-[11px] uppercase tracking-widest text-[#E9840D] font-semibold">Teknik Diesel Indonesia</span>
            </div>
            <h1 className="font-display font-extrabold text-[#171717] leading-[1.05] tracking-tight mb-5" style={{ fontSize: "clamp(2.4rem, 4.8vw, 4.25rem)" }}>
              {settings?.hero_headline ? settings.hero_headline : <>Solusi Mesin & Peralatan Industri <span className="text-[#FF9418]">Terpercaya</span></>}
            </h1>
            <p className="text-base lg:text-lg text-[#555555] leading-relaxed mb-8 max-w-xl">
              {settings?.hero_subtitle || "Teknik Diesel Indonesia menyediakan kompresor portable, bar bender, bar cutter, bar roller, spare parts, serta berbagai kebutuhan mesin dan peralatan industri untuk proyek dan workshop."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/produk">
                <Button className="h-12 px-8 bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg font-bold uppercase tracking-wider text-sm shadow-md" data-testid="hero-cta-produk">
                  Lihat Produk <ArrowRight size={16} />
                </Button>
              </Link>
              <Link to="/hubungi-kami">
                <Button variant="outline" className="h-12 px-8 border-2 border-[#171717] text-[#171717] hover:bg-[#171717] hover:text-white rounded-lg font-bold uppercase tracking-wider text-sm bg-white" data-testid="hero-cta-contact">
                  Hubungi Kami
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs font-mono-tech text-[#737373] uppercase tracking-widest flex-wrap">
              <span className="flex items-center gap-2"><span className="w-2 h-2 bg-[#FF9418] rounded-full" /> Airman • Takeda • Toyo • Akurad</span>
            </div>
          </div>
          <div className="relative">
            {settings?.hero_image ? (
              <div className="relative aspect-[5/4] overflow-hidden rounded-2xl border border-[#E5E5E5] shadow-xl bg-[#F8F8F8]">
                <img src={settings.hero_image} alt="Teknik Diesel Indonesia — Workshop" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="relative aspect-[5/4] overflow-hidden rounded-2xl border border-[#E5E5E5] shadow-sm bg-white flex items-center justify-center p-10" style={{backgroundImage: "repeating-linear-gradient(45deg, transparent 0 14px, rgba(255,148,24,0.04) 14px 28px)"}}>
                <div className="text-center">
                  <img src="/icon-192.png" alt="" className="w-20 h-20 mx-auto mb-4 opacity-80" />
                  <div className="font-display font-black text-[#171717] text-3xl lg:text-4xl leading-tight mb-2">Teknik Diesel<br/>Indonesia</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF9418] font-bold">General Technical Supply</div>
                  <div className="mt-6 grid grid-cols-2 gap-2 max-w-xs mx-auto">
                    {["Kompresor", "Bar Bender", "Bar Cutter", "Spare Parts"].map((t) => (
                      <div key={t} className="bg-[#F8F8F8] border border-[#E5E5E5] rounded px-2 py-1.5 font-display font-semibold text-[#171717] text-xs">{t}</div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-[#F8F8F8] border-b border-[#E5E5E5]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 py-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {TRUST.map((t) => (
            <div key={t.title} className="flex flex-col gap-3">
              <div className="w-12 h-12 flex items-center justify-center bg-[#FFF4E8] border border-[#FFD3A2] rounded-lg">
                <t.icon size={22} className="text-[#FF9418]" />
              </div>
              <h3 className="font-display font-bold text-[#171717] text-lg">{t.title}</h3>
              <p className="text-sm text-[#555555] leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES — dynamic from Admin > Categories (show_on_homepage=true, respects sort_order) */}
      <section className="max-w-7xl mx-auto px-4 py-12 lg:py-16" data-testid="categories-section">
        <div className="flex items-end justify-between mb-6 lg:mb-8 gap-4">
          <div>
            <SectionLabel>Kategori Produk</SectionLabel>
            <h2 className="font-display font-extrabold text-[#171717] text-2xl sm:text-3xl lg:text-4xl">Jelajahi Kategori</h2>
          </div>
          <Link to="/produk" className="hidden md:inline-flex text-sm text-[#555555] hover:text-[#FF9418] items-center gap-1 font-semibold shrink-0">Semua Produk <ArrowRight size={14} /></Link>
        </div>
        {homepageCategories.length === 0 ? (
          <div className="text-sm text-[#737373] py-6 border border-dashed border-[#E5E5E5] rounded-lg text-center">Belum ada kategori aktif.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4">
            {homepageCategories.map((c) => <CategoryCard key={c.id || c.slug} cat={c} />)}
          </div>
        )}
        <div className="mt-6 md:hidden">
          <Link to="/produk" className="inline-flex items-center gap-1 text-sm font-semibold text-[#FF9418]">Semua Produk <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="bg-[#F8F8F8] border-y border-[#E5E5E5]" data-testid="featured-products-section">
          <div className="max-w-7xl mx-auto px-4 py-16">
            <div className="flex items-end justify-between mb-8">
              <div>
                <SectionLabel>Produk Unggulan</SectionLabel>
                <h2 className="font-display font-extrabold text-[#171717] text-3xl lg:text-4xl">Rekomendasi Mesin</h2>
              </div>
              <Link to="/produk" className="hidden md:inline-flex text-sm text-[#555555] hover:text-[#FF9418] items-center gap-1 font-semibold">Lihat Semua <ArrowRight size={14} /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {featured.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* AIRMAN SPOTLIGHT */}
      <section className="section-highlight border-b border-[#E5E5E5]" data-testid="airman-section">
        <div className="max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <SectionLabel>Portable Air Compressor</SectionLabel>
            <h2 className="font-display font-extrabold text-[#171717] text-3xl lg:text-5xl leading-tight mb-5">Portable Air Compressor <span className="text-[#FF9418]">Airman</span></h2>
            <p className="text-[#555555] leading-relaxed mb-6">
              Teknik Diesel Indonesia menyediakan berbagai model Airman PDS untuk kebutuhan proyek konstruksi, blasting, hingga aplikasi industri. Pilih model sesuai kebutuhan air delivery dan working pressure Anda.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {["PDS 175S", "PDS 185S", "PDS 390S", "PDS 750S"].map((m) => (
                <Link key={m} to={`/produk/airman-${m.toLowerCase().replace(/\s+/g,'-')}`} className="bg-white border border-[#E5E5E5] hover:border-[#FF9418] hover:bg-[#FFF4E8] rounded-lg px-4 py-3 flex items-center justify-between group transition" data-testid={`airman-quick-${m.toLowerCase().replace(/\s+/g,'-')}`}>
                  <span className="font-display font-semibold text-sm text-[#171717] group-hover:text-[#FF9418]">Airman {m}</span>
                  <ArrowRight size={14} className="text-[#737373] group-hover:text-[#FF9418]" />
                </Link>
              ))}
            </div>
            <Link to="/produk?category=Portable Air Compressor">
              <Button className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg h-11 px-6 uppercase tracking-wider font-bold text-sm shadow-md">Lihat Kompresor Airman</Button>
            </Link>
          </div>
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-[#FFD3A2] bg-white shadow-lg p-8 flex flex-col justify-between" style={{backgroundImage: "repeating-linear-gradient(45deg, transparent 0 14px, rgba(255,148,24,0.06) 14px 28px)"}}>
            <div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF9418] mb-2 font-bold">Airman</div>
              <div className="font-display font-black text-[#171717] text-4xl lg:text-5xl leading-none mb-1">PDS Series</div>
              <div className="font-display text-[#555555] text-lg">Portable Air Compressor</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["PDS 175S", "PDS 185S", "PDS 390S", "PDS 750S"].map((m) => (
                <div key={m} className="bg-white border border-[#E5E5E5] px-3 py-2 rounded font-display font-bold text-[#171717] text-sm text-center">{m}</div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BENDER / CUTTER */}
      <section className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative aspect-square overflow-hidden rounded-2xl border border-[#E5E5E5] bg-[#F8F8F8] shadow-lg order-2 lg:order-1 p-8 flex flex-col justify-between">
            <div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#FF9418] mb-2 font-bold">Takeda • Toyo</div>
              <div className="font-display font-black text-[#171717] text-4xl lg:text-5xl leading-none mb-1">Bar Bender</div>
              <div className="font-display font-black text-[#171717] text-4xl lg:text-5xl leading-none mb-2">Bar Cutter</div>
              <div className="font-display text-[#555555] text-lg">Construction Machinery</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {["B32", "B42", "B52", "C32", "C42", "C52"].map((m) => (
                <div key={m} className="bg-white border border-[#E5E5E5] px-2 py-2 rounded font-display font-bold text-[#171717] text-sm text-center">{m}</div>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <SectionLabel>Konstruksi & Workshop</SectionLabel>
            <h2 className="font-display font-extrabold text-[#171717] text-3xl lg:text-5xl leading-tight mb-5">Bar Bender & <span className="text-[#FF9418]">Bar Cutter</span></h2>
            <p className="text-[#555555] leading-relaxed mb-6">
              Mesin pembengkok dan pemotong besi tulangan untuk proyek konstruksi dengan pilihan brand Takeda dan Toyo dalam berbagai kapasitas.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/produk?category=Bar Bender"><Button className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg h-11 px-6 uppercase tracking-wider font-bold text-sm">Bar Bender</Button></Link>
              <Link to="/produk?category=Bar Cutter"><Button variant="outline" className="border-2 border-[#171717] text-[#171717] hover:bg-[#171717] hover:text-white rounded-lg h-11 px-6 uppercase tracking-wider font-bold text-sm bg-white">Bar Cutter</Button></Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="bg-[#171717] rounded-2xl p-8 lg:p-12 flex flex-col lg:flex-row items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#FF9418] rounded-full opacity-20 blur-3xl" />
          <div className="relative">
            <h3 className="font-display font-extrabold text-white text-2xl lg:text-3xl mb-2">Butuh Konsultasi Mesin Industri?</h3>
            <p className="text-[#B5B5B5]">Tim Teknik Diesel Indonesia siap membantu kebutuhan Anda melalui WhatsApp.</p>
          </div>
          <a href={buildWhatsAppUrl(wa, generalWhatsAppMessage())} target="_blank" rel="noopener noreferrer" className={`relative ${wa ? "" : "hidden"}`}>
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-lg h-12 px-8 uppercase tracking-wider font-bold text-sm shadow-lg" data-testid="cta-consult-whatsapp">Konsultasi Sekarang</Button>
          </a>
          {!wa && (
            <Link to="/hubungi-kami" className="relative">
              <Button className="bg-[#FF9418] hover:bg-[#E9840D] text-white rounded-lg h-12 px-8 uppercase tracking-wider font-bold text-sm shadow-lg">Hubungi Kami</Button>
            </Link>
          )}
        </div>
      </section>

      {latest.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 pb-20">
          <div className="mb-8">
            <SectionLabel>Produk Terbaru</SectionLabel>
            <h2 className="font-display font-extrabold text-[#171717] text-3xl">Terbaru di Katalog</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {latest.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
